import React, { FC, useCallback, useMemo } from "react";
import _ from "lodash";
import { connect, Dispatch, MapDispatchToPropsParam } from "react-redux";
import { ICellRendererParams, RowNode } from "ag-grid-community";
import MenuItem from "@material-ui/core/MenuItem";
import MoreMenu from "./MoreMenu";
import { format } from "lib/globalize-util";
import { IStore } from "reducers";
import type { IScope } from "reducers/scope";
import {
    addLineItem,
    handleBuildOrderOpen,
    handleDeleteDelivery,
    handleEditDelivery,
    updateLineItem,
    updateWorkingOrder,
} from "actions/cart-legacy";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";
import { postToTopIframe, postToWindow } from "helpers/window-messages";
import { DetailGridContext, IRowData, ISize } from "components/shipment-review/types";
import { getAllocatedEntries } from "../helpers/gridEntry";
import { isMatchLocation } from "helpers/legacy-cart/bulk";
import { AllocationType } from "types/cart";
import { getRowRange } from "containers/legacy-cart/helpers/common";
import { BufferContext, GridBuffer, PasteSizeData } from "constants/sizingView";
import cartGridViewActions from "actions/cart-grid-view";
import { getGridBuffer, getPasteActions } from "../helpers/copyPasteHandlers";
import { DATATEST } from "constants/integration";

type IOwnProps = CastProp<CastProp<ICellRendererParams, "context", DetailGridContext>, "data", IRowData> & {
    uniqueSizes: ISize[];
};

interface IStateProps {
    hasLocations: boolean;
    hasCombined: boolean;
    scope: IScope;
    buffer: GridBuffer;
}

type IDispatchProps = { updateBuffer: (buffer: GridBuffer) => void };

const DetailsMenu: FC<IOwnProps & IStateProps & IDispatchProps> = (props) => {
    const { data, context } = props;
    const { scope, hasCombined, hasLocations, uniqueSizes, updateBuffer, buffer } = props;

    const buildOrderEnabled = useMemo(() => scope?.brand?.hasPreference("build_order"), [scope]);
    const isPasteDisabled = useMemo(
        () => !data.available || buffer.context !== BufferContext.SizesRow || !_.size(buffer.entries),
        [data.available, buffer],
    );
    const isCopyPasteAvailable = useMemo(() => !buildOrderEnabled && isMatchLocation(data), [buildOrderEnabled, data]);

    const onEditDelivery = useCallback(() => {
        if (buildOrderEnabled) {
            postToTopIframe(handleBuildOrderOpen());
        } else {
            postToWindow(handleEditDelivery({ _id: data?.lineItemId }));
        }
    }, [buildOrderEnabled, data?.lineItemId]);

    const onDeleteDelivery = useCallback(() => {
        if (buildOrderEnabled) {
            postToTopIframe(handleBuildOrderOpen());
        } else {
            postToTopIframe(handleDeleteDelivery(data?.lineItemId));
        }
    }, [buildOrderEnabled, data?.lineItemId]);

    const changeAllocated = useCallback(() => {
        const update = {
            ...getRowRange(data.splitFields),
            allocated: data.allocated === AllocationType.Combined ? AllocationType.ByLocation : AllocationType.Combined,
        };
        const action = data.lineItemId ? updateLineItem(data.lineItemId, update) : addLineItem(data.item.id, update);
        postToWindow(updateWorkingOrder(action));
    }, [data]);

    const handleCopy = useCallback(() => {
        updateBuffer(getGridBuffer<IRowData>(BufferContext.SizesRow, { rows: [data], columns: uniqueSizes }));
    }, [updateBuffer, data, uniqueSizes]);
    const handlePasteQTY = useCallback(() => {
        const actions = getPasteActions<IRowData>(BufferContext.Quantity, buffer, {
            rows: [data],
            columns: uniqueSizes,
        });
        if (_.size(actions)) {
            postToWindow(updateWorkingOrder(actions));
        }
    }, [buffer, data, uniqueSizes]);
    const handlePasteSizes = useCallback(() => {
        const rows = _.map(data.sizes, ({ quantity, size }) => ({
            item: data.item,
            quantity,
            size,
        }));
        const actions = getPasteActions<PasteSizeData>(BufferContext.Sizes, buffer, { rows, columns: [] });
        if (_.size(actions)) {
            postToWindow(updateWorkingOrder(actions));
        }
    }, [buffer, data]);
    const handlePasteToAll = useCallback(() => {
        const rows = [];
        context.parentApi.forEachNodeAfterFilter(({ data, group }: CastProp<RowNode, "data", IGridItem>) => {
            if (group) {
                return;
            }
            rows.push(
                ..._.map(getAllocatedEntries(data.entries), (entry) => ({
                    item: entry,
                    quantity: entry?.quantity,
                    size: entry?.size,
                })),
            );
        });
        const actions = getPasteActions<PasteSizeData>(BufferContext.Sizes, buffer, { rows, columns: [] });
        if (_.size(actions)) {
            postToWindow(updateWorkingOrder(actions));
        }
    }, [context.parentApi, buffer]);

    return (
        <MoreMenu closeOnClick>
            {isCopyPasteAvailable && [
                <MenuItem key="copy" onClick={handleCopy} data-test={DATATEST.GRID_VIEW.DETAILS_MENU.COPY_QTY}>
                    {format("Copy quantities")}
                </MenuItem>,
                <MenuItem
                    key="paste-qty"
                    onClick={handlePasteQTY}
                    disabled={isPasteDisabled}
                    data-test={DATATEST.GRID_VIEW.DETAILS_MENU.PASTE_QTY}
                >
                    {format("Paste quantities")}
                </MenuItem>,
                <MenuItem
                    key="paste-qty-by-size"
                    onClick={handlePasteSizes}
                    disabled={isPasteDisabled}
                    data-test={DATATEST.GRID_VIEW.DETAILS_MENU.PASTE_QTY_BY_SIZE}
                >
                    {format("Paste quantities by size")}
                </MenuItem>,
                <MenuItem
                    key="paste-qty-to-sizes"
                    onClick={handlePasteToAll}
                    disabled={isPasteDisabled}
                    data-test={DATATEST.GRID_VIEW.DETAILS_MENU.PASTE_QTY_TO_SIZES}
                >
                    {format("Paste quantities by size across cart")}
                </MenuItem>,
            ]}
            {hasCombined && (data.isVirtual || data.allocated === AllocationType.ByLocation) && (
                <MenuItem onClick={changeAllocated} data-test={DATATEST.GRID_VIEW.DETAILS_MENU.ENABLE_ALL_LOCATIONS}>
                    {format("Enable All Locations Combined")}
                </MenuItem>
            )}
            {hasLocations && (data.isVirtual || data.allocated === AllocationType.Combined) && (
                <MenuItem onClick={changeAllocated} data-test={DATATEST.GRID_VIEW.DETAILS_MENU.ENABLE_BY_LOCATION}>
                    {format("Enable By Location")}
                </MenuItem>
            )}
            <MenuItem onClick={onEditDelivery} data-test={DATATEST.GRID_VIEW.DETAILS_MENU.EDIT_DELIVERY}>
                {format("Edit delivery")}
            </MenuItem>
            <MenuItem
                disabled={context.hasSingleDelivery}
                onClick={onDeleteDelivery}
                data-test={DATATEST.GRID_VIEW.DETAILS_MENU.REMOVE_DELIVERY}
            >
                {format("Remove delivery")}
            </MenuItem>
            <MenuItem
                style={{ display: "none" }}
                disabled={true}
                data-test={DATATEST.GRID_VIEW.DETAILS_MENU.GROUP_BY_DELIVERY}
            >
                {format("Group by delivery")}
            </MenuItem>
        </MoreMenu>
    );
};

const mapStateToProps = ({ cartGridView, scope }: IStore): IStateProps => ({
    hasLocations: _.some(cartGridView.data.locations, "id"),
    hasCombined: _.some(cartGridView.data.locations, { id: null }), // id: null is a reserved value
    scope,
    buffer: cartGridView.buffer,
});

const mapDispatchToProps: MapDispatchToPropsParam<IDispatchProps, ICellRendererParams> = (dispatch: Dispatch<any>) => ({
    updateBuffer: (buffer: GridBuffer) => dispatch(cartGridViewActions.update({ buffer })),
});

export default connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(DetailsMenu);
